import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AdminService } from "@/gen/omnios/v1/admin_connect.js";
import { formatCurrency } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Printer } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/invoices/$id")({
	component: InvoicePage,
});

const adminClient = createClient(AdminService, transport);

function InvoicePage() {
	const { t } = useTranslation("admin");
	const { id } = Route.useParams();

	const STATUS_LABELS: Record<string, string> = {
		draft: t("invoices_status_draft"),
		finalized: t("invoices_status_finalized"),
		sent: t("invoices_status_sent"),
		paid: t("invoices_status_paid"),
		void: t("invoices_status_void"),
	};

	const LINE_TYPE_LABELS: Record<string, string> = {
		base_fee: t("invoice_line_base_fee"),
		seat_charge: t("invoice_line_seat_charge"),
		feature_fee: t("invoice_line_feature_fee"),
		plugin_fee: t("invoice_line_plugin_fee"),
		usage: t("invoice_line_usage"),
		adjustment: t("invoice_line_adjustment"),
	};

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "invoice", id],
		queryFn: () => adminClient.getInvoice({ invoiceId: id }),
	});

	if (isLoading) {
		return (
			<div className="space-y-6 max-w-3xl mx-auto">
				<Skeleton className="h-8 w-48" />
				<Skeleton className="h-96" />
			</div>
		);
	}

	const inv = data?.invoice;
	if (!inv) {
		return (
			<div className="text-center py-12">
				<p className="text-muted-foreground">{t("invoice_not_found")}</p>
				<Button asChild className="mt-4" variant="outline">
					<Link to="/admin/workspaces">{t("common:back")}</Link>
				</Button>
			</div>
		);
	}

	const subtotal = Number(inv.subtotal);
	const tax = Number(inv.taxAmount);
	const total = Number(inv.total);

	return (
		<div>
			{/* Screen-only toolbar */}
			<div className="flex items-center justify-between mb-6 print:hidden">
				<Button variant="ghost" size="sm" onClick={() => window.history.back()}>
					<ArrowLeft className="h-4 w-4 mr-1" />
					{t("common:back")}
				</Button>
				<Button size="sm" onClick={() => window.print()}>
					<Printer className="h-4 w-4 mr-1" />
					{t("invoice_download_pdf")}
				</Button>
			</div>

			{/* Printable invoice */}
			<div className="max-w-3xl mx-auto bg-background border rounded-lg print:border-0 print:shadow-none print:rounded-none">
				<div className="p-8 print:p-0 space-y-8 text-sm">
					{/* Header */}
					<div className="flex justify-between items-start">
						<div>
							<h1 className="text-2xl font-bold">{t("invoice_title")}</h1>
							<p className="text-muted-foreground mt-1 font-mono">
								{inv.invoiceNumber}
							</p>
						</div>
						<div className="text-right">
							<Badge
								variant={
									inv.status === "paid"
										? "default"
										: inv.status === "void"
											? "outline"
											: "secondary"
								}
								className="print:border print:border-current"
							>
								{STATUS_LABELS[inv.status] ?? inv.status}
							</Badge>
						</div>
					</div>

					{/* Seller / Buyer */}
					<div className="grid grid-cols-2 gap-8">
						<div>
							<p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
								{t("invoice_seller")}
							</p>
							<p className="font-medium">OmniOS</p>
							<p className="text-muted-foreground">
								{t("invoice_seller_company")}
							</p>
						</div>
						<div>
							<p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
								{t("invoice_buyer")}
							</p>
							<p className="font-medium">
								Workspace {inv.workspaceId.slice(0, 8)}
							</p>
						</div>
					</div>

					{/* Dates */}
					<div className="grid grid-cols-3 gap-4 border-t border-b py-4">
						<div>
							<p className="text-xs text-muted-foreground">
								{t("invoice_period")}
							</p>
							<p>
								{inv.periodStart || "—"} — {inv.periodEnd || "—"}
							</p>
						</div>
						<div>
							<p className="text-xs text-muted-foreground">
								{t("invoice_issue_date")}
							</p>
							<p>
								{inv.createdAt
									? new Date(
											Number(inv.createdAt.seconds) * 1000,
										).toLocaleDateString("ru-RU")
									: "—"}
							</p>
						</div>
						<div>
							<p className="text-xs text-muted-foreground">
								{t("invoice_due_date")}
							</p>
							<p>{inv.dueDate || "—"}</p>
						</div>
					</div>

					{/* Line items */}
					<Table>
						<TableHeader>
							<TableRow className="border-b text-xs text-muted-foreground uppercase tracking-wider">
								<TableHead className="text-left py-2 font-medium h-auto">
									{t("invoice_col_description")}
								</TableHead>
								<TableHead className="text-right py-2 font-medium w-20 h-auto">
									{t("invoice_col_quantity")}
								</TableHead>
								<TableHead className="text-right py-2 font-medium w-28 h-auto">
									{t("invoice_col_price")}
								</TableHead>
								<TableHead className="text-right py-2 font-medium w-28 h-auto">
									{t("invoice_col_amount")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{inv.lineItems.map((line, i) => (
								<TableRow key={line.id || i} className="border-b last:border-0">
									<TableCell className="py-2.5">
										<p>{line.description}</p>
										<p className="text-xs text-muted-foreground">
											{LINE_TYPE_LABELS[line.lineType] ?? line.lineType}
											{line.metricCode ? ` · ${line.metricCode}` : ""}
										</p>
									</TableCell>
									<TableCell className="text-right py-2.5 tabular-nums">
										{Number(line.quantity).toLocaleString()}
									</TableCell>
									<TableCell className="text-right py-2.5 tabular-nums">
										{formatCurrency(line.unitPrice)}
									</TableCell>
									<TableCell className="text-right py-2.5 tabular-nums font-medium">
										{formatCurrency(line.amount)}
									</TableCell>
								</TableRow>
							))}

							{inv.lineItems.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={4}
										className="text-center text-muted-foreground py-6"
									>
										{t("invoice_no_items")}
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>

					{/* Totals */}
					<div className="flex justify-end">
						<div className="w-64 space-y-1.5">
							<div className="flex justify-between">
								<span className="text-muted-foreground">
									{t("invoice_subtotal")}
								</span>
								<span className="tabular-nums">{formatCurrency(subtotal)}</span>
							</div>
							{tax > 0 && (
								<div className="flex justify-between">
									<span className="text-muted-foreground">
										{t("invoice_vat")}
									</span>
									<span className="tabular-nums">{formatCurrency(tax)}</span>
								</div>
							)}
							<div className="flex justify-between border-t pt-1.5 font-semibold text-base">
								<span>{t("invoice_total")}</span>
								<span className="tabular-nums">{formatCurrency(total)}</span>
							</div>
						</div>
					</div>

					{/* Payments */}
					{inv.payments.length > 0 && (
						<div>
							<p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
								{t("invoice_payments")}
							</p>
							<div className="space-y-1.5">
								{inv.payments.map((p) => (
									<div key={p.id} className="flex justify-between text-sm">
										<div>
											<span>
												{p.method === "bank_transfer"
													? t("invoice_payment_bank")
													: p.method === "stripe"
														? "Stripe"
														: t("invoice_payment_manual")}
											</span>
											{p.referenceNumber && (
												<span className="text-muted-foreground ml-2">
													#{p.referenceNumber}
												</span>
											)}
										</div>
										<span className="tabular-nums font-medium">
											{formatCurrency(p.amount)}
										</span>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Notes */}
					{inv.notes && (
						<div className="border-t pt-4">
							<p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
								{t("invoice_notes")}
							</p>
							<p className="text-muted-foreground">{inv.notes}</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
